import 'react';
import {
	Button,
	Checkbox,
	Form,
	message,
	Modal,
	Select,
	Typography,
	Tag,
	ConfigProvider,
} from 'antd';

import { useUpdateOrderLineItemMutation } from '../../../redux/api/order.ts';
import { GetQueryId, Lineitem } from '../../../interfaces/Ordel.Interface.ts';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { statuses } from '../../../config/constants.ts';
import { useCompleteOrderLineItemMutation, useLazyGetGroupedRelatedMaterialReservationsQuery } from '../../../redux/api/order-line-item.ts';
import { CheckCircleFilled } from '@ant-design/icons';
import { getOrderLineItemProductionStatusTagColor } from '../../../utils/index.ts';
import { THEME } from '../../../main.tsx';
import { useBallooningStatusChange } from '../../../hooks/useBallooningStatusChange';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import {
	PRODUCTION_QUESTION_BLOCK_HINT,
	getProductionQuestionBlockMessage,
} from '../../../utils/productionQuestionBlock';

const { Text } = Typography;

interface Props {
	disabled: boolean;
	lineItem: Lineitem;
	refetchLineItem: () => void;
}

const OrderLineItemStatuses = (props: Props) => {
	const [messageApi, contextHolder] = message.useMessage();
	const navigate = useNavigate();

	const [form] = Form.useForm();

	const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);

	const [updateOrderLineItem, updateOrderLineItemResult] =
		useUpdateOrderLineItemMutation();
	const [completeOrderLineItem, completeOrderLineItemResult] =
		useCompleteOrderLineItemMutation();
	const [triggerGetGroupedReservations] = useLazyGetGroupedRelatedMaterialReservationsQuery();

	const { handleBallooningStatusChange } = useBallooningStatusChange();
	const [isOverrideDeliveryModalOpen, setIsOverrideDeliveryModalOpen] = useState(false);
	const [pendingDeliveryValue, setPendingDeliveryValue] = useState<string | null>(null);
	const [
		isProgrammingTemplateModalOpen,
		setIsProgrammingTemplateModalOpen,
	] = useState(false);
	const [pendingProgrammingTemplateValue, setPendingProgrammingTemplateValue] =
		useState<string | null>(null);
	const [productionQuestionBlock, setProductionQuestionBlock] = useState<
		string | null
	>(null);

	const scrollToProductionQuestionsSection = () => {
		const el = document.getElementById('production-questions-section');
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	};

	async function handleChangeStatus(field_name: string, value: string) {
		if (!props.lineItem) return;

		if (field_name === 'ballooning_status') {
			const currentStatus = (props.lineItem as any)['ballooning_status'] as string | undefined;
			await handleBallooningStatusChange({
				lineItem: props.lineItem,
				newStatus: value,
				prevStatus: currentStatus,
				options: { navigateOnInProgress: true, navigateOnCompleted: false },
			});
			props.refetchLineItem();
			return;
		}

		// Apply Material Prep UX parity with queue
		if (field_name === 'material_prep_status') {
			// Front-end validation mirroring backend rule
			if (
				(value === 'Completed' || value === 'In Progress') &&
				props.lineItem.cam_programming_status !== 'Completed'
			) {
				messageApi.open({ type: 'warning', content: 'CAM Programming needs to be completed prior to updating Material Prep.' });
				return;
			}

			// Route to Cutting when moving Material Prep to In Progress
			if (value === 'In Progress') {
				navigate(`/tablet/operations/cutting?group_cut_status=Pending&search=${props.lineItem.name}`);
				return;
			}

			// Prevent marking as Completed when nothing has been cut
			if (value === 'Completed') {
				try {
					const res = await triggerGetGroupedReservations({ id: props.lineItem.id }).unwrap();
					const groups = (res as any[]) || [];
					const anyCut = groups.some((g: any) =>
						Array.isArray(g?.reservations) && g.reservations.some((r: any) => (r?.material_cut_status === 'Cut') || ((r?.actual_cut_quantity || 0) > 0))
					);
					if (!anyCut) {
						Modal.confirm({
							centered: true,
							title: 'Material Prep Requires Cutting',
							icon: null,
							content:
								'No material appears to be cut for this job. Please cut material in the Material (Cutting) section before marking Material Prep as Completed.',
							onOk: () => navigate(`/tablet/operations/cutting?group_cut_status=Pending&search=${props.lineItem.name}`),
							onCancel: () => { },
							footer: (_, { OkBtn, CancelBtn }) => (
								<ConfigProvider theme={THEME}>
									<CancelBtn />
									<OkBtn />
								</ConfigProvider>
							),
						});
						return;
					}
				} catch (e) {
					Modal.confirm({
						centered: true,
						title: 'Material Prep Requires Cutting',
						icon: null,
						content:
							'Unable to verify cutting status. Please proceed to the Material (Cutting) section to complete cutting before marking as Completed.',
						onOk: () => navigate(`/tablet/operations/cutting?group_cut_status=Pending&search=${props.lineItem.name}`),
						onCancel: () => { },
						footer: (_, { OkBtn, CancelBtn }) => (
							<ConfigProvider theme={THEME}>
								<CancelBtn />
								<OkBtn />
							</ConfigProvider>
						),
					});
					return;
				}
			}
		}

		updateOrderLineItem({
			id: props.lineItem.id,
			values: {
				[field_name]: value,
			},
		})
			.unwrap()
			.then(() => {
				props.refetchLineItem();

				messageApi.open({
					type: 'success',
					content: `Status updated successfully`,
				});
			})
			.catch((error: unknown) => {
				const block = getProductionQuestionBlockMessage(error);
				if (block) {
					setProductionQuestionBlock(block);
					return;
				}

				messageApi.open({
					type: 'error',
					content: getApiErrorMessage(error, 'Failed to update status'),
				});
			});
	}

	function getDeliveredStatus(): 'Not Started' | 'Partial Delivery' | 'Delivered in Full' {
		// prefer backend provided delivery_status if present
		const backend = (props.lineItem as any)?.delivery_status as string | undefined;
		if (backend && ['Not Started', 'Partial Delivery', 'Delivered in Full'].includes(backend)) {
			return backend as any;
		}
		const delivered = props.lineItem?.delivered_quantity ?? 0;
		const qty = props.lineItem?.quantity ?? 0;
		if (!qty || delivered <= 0) return 'Not Started';
		if (delivered >= qty) return 'Delivered in Full';
		return 'Partial Delivery';
	}

	async function applyDeliveryOverride() {
		if (!props.lineItem || !pendingDeliveryValue) return;
		try {
			await updateOrderLineItem({
				id: props.lineItem.id,
				values: { delivery_status: pendingDeliveryValue },
			}).unwrap();
			messageApi.open({ type: 'success', content: `Delivery status updated to "${pendingDeliveryValue}"` });
			setIsOverrideDeliveryModalOpen(false);
			setPendingDeliveryValue(null);
			props.refetchLineItem();
		} catch (error: any) {
			messageApi.open({ type: 'error', content: error?.data?.message || 'Failed to update delivery status' });
		}
	}

	function shouldConfirmProgrammingTemplateChange(
		currentStatus: string | null | undefined,
		newStatus: string
	) {
		if (newStatus === 'Completed' && currentStatus !== 'Completed') {
			return true;
		}
		return (
			currentStatus === 'Not Started' && newStatus === 'In Progress'
		);
	}

	function handleProgrammingTemplateStatusSelect(value: string) {
		const currentStatus = props.lineItem.programming_template_status;
		if (shouldConfirmProgrammingTemplateChange(currentStatus, value)) {
			setPendingProgrammingTemplateValue(value);
			setIsProgrammingTemplateModalOpen(true);
			return;
		}
		handleChangeStatus('programming_template_status', value);
	}

	async function applyProgrammingTemplateOverride() {
		if (!pendingProgrammingTemplateValue) {
			return;
		}
		await handleChangeStatus(
			'programming_template_status',
			pendingProgrammingTemplateValue
		);
		setIsProgrammingTemplateModalOpen(false);
		setPendingProgrammingTemplateValue(null);
	}

	function goToProgramScoring() {
		setIsProgrammingTemplateModalOpen(false);
		setPendingProgrammingTemplateValue(null);

		const tryScroll = (attempt = 0) => {
			const el = document.getElementById('program-scoring-section');
			if (el) {
				el.scrollIntoView({ behavior: 'smooth', block: 'start' });
				return;
			}
			if (attempt < 10) {
				setTimeout(() => tryScroll(attempt + 1), 100);
			}
		};

		// Give the modal a moment to unmount so scroll position is stable.
		setTimeout(() => tryScroll(0), 50);
	}

	function handleModalClose() {
		setIsChecklistModalOpen(false);
		form.resetFields();
	}

	async function handleCompleteOrder(lineItemId: string) {
		const queryId: GetQueryId = {
			id: lineItemId,
		};

		completeOrderLineItem(queryId)
			.unwrap()
			.then((data) => {
				handleModalClose();
				props.refetchLineItem();

				messageApi.open({
					type: 'success',
					content: data?.message,
				});
			})
			.catch((error) =>
				messageApi.open({
					type: 'error',
					content: error.data?.message,
				})
			);
	}

	return !props.lineItem ? null : (
		<>
			<div className="flex flex-col gap-3 wrap-no-wrap">
				{statuses.map((category, index) => {
					function hideStatusCategory() {
						if (
							category.field_name === 'keyence_programming_status' &&
							props.lineItem.is_keyence_program_required === false
						) {
							return true;
						}

						if (
							category.field_name === 'tooling_ordering_status' &&
							props.lineItem.is_additional_tooling_required === false
						) {
							return true;
						}

						return false;
					}

					// Determine current status; for delivery_status compute from delivered quantities
					const currentStatus =
						category.field_name === 'delivery_status'
							? getDeliveredStatus()
							: Object.entries(props.lineItem).filter(
								(x) => x[0] === category.field_name
							).length > 0
								? Object.entries(props.lineItem).filter(
									(x) => x[0] === category.field_name
								)[0][1]
								: null;

					return hideStatusCategory() ? null : (
						<div key={index}>
							<Text strong>{category.label}</Text>
							<Select
								style={{
									width: '100%',
								}}
								placeholder="Select a status"
								value={currentStatus}
								popupMatchSelectWidth={false}
								onChange={(value) => {
									if (category.field_name === 'delivery_status') {
										setPendingDeliveryValue(value);
										setIsOverrideDeliveryModalOpen(true);
									} else if (category.field_name === 'programming_template_status') {
										handleProgrammingTemplateStatusSelect(value);
									} else {
										handleChangeStatus(category.field_name, value);
									}
								}}
								disabled={
									props.disabled ||
									updateOrderLineItemResult.isLoading
								}
							>
								{category.statuses.map((status, index) => (
									<Select.Option key={index} value={status}>
										<Tag color={getOrderLineItemProductionStatusTagColor(status)}>
											{status}
										</Tag>
									</Select.Option>
								))}
							</Select>
						</div>
					);
				})}

				{props.lineItem.machining_status === 'Completed' ||
					props.lineItem.status === 'Completed' ? (
					<Button
						type="primary"
						onClick={() => setIsChecklistModalOpen(true)}
						disabled={props.disabled}
					>
						{props.lineItem.status === 'Completed' ? (
							<>
								<CheckCircleFilled /> Completed
							</>
						) : (
							<>Mark as Completed</>
						)}
					</Button>
				) : null}
			</div>

			<Modal
				open={isChecklistModalOpen}
				centered
				width={640}
				onCancel={handleModalClose}
				title="Line Item Completion Checklist"
				footer={null}
			>
				<div className="pt-4">
					<Form
						form={form}
						layout="vertical"
						onFinish={() => handleCompleteOrder(props.lineItem.id)}
					>
						<Form.Item
							name="fai-completed-checkbox"
							valuePropName="checked"
							rules={[
								{
									type: 'boolean',
									required: true,
									message: 'Required',
									validator: async (_, checked) => {
										if (!checked) {
											return Promise.reject();
										}
									},
								},
							]}
						>
							<Checkbox>
								The FAI Report has been completed and/or a Keyence/CMM Report
								exists
							</Checkbox>
						</Form.Item>
						<Form.Item
							name="required-quantity-verified-checkbox"
							valuePropName="checked"
							rules={[
								{
									type: 'boolean',
									required: true,
									message: 'Required',
									validator: async (_, checked) => {
										if (!checked) {
											return Promise.reject();
										}
									},
								},
							]}
						>
							<Checkbox>
								The required quantity of parts were dropped off at the shipping
								department
							</Checkbox>
						</Form.Item>

						<div className="flex flex-row justify-end gap-1 pt-2">
							<Button htmlType="button" onClick={handleModalClose}>
								Cancel
							</Button>
							<Button
								type="primary"
								disabled={props.lineItem.status === 'Completed'}
								loading={completeOrderLineItemResult?.isLoading}
								htmlType="submit"
							>
								Mark as Completed
							</Button>
						</div>
					</Form>
				</div>
			</Modal>
			{contextHolder}

			<Modal
				open={isOverrideDeliveryModalOpen}
				centered
				title="Override Delivered Status"
				onCancel={() => { setIsOverrideDeliveryModalOpen(false); setPendingDeliveryValue(null); }}
				onOk={applyDeliveryOverride}
				okButtonProps={{ disabled: !pendingDeliveryValue || updateOrderLineItemResult.isLoading }}
				confirmLoading={updateOrderLineItemResult.isLoading}
			>
				<Typography.Paragraph>
					You are about to manually change the Delivered status to:
				</Typography.Paragraph>
				<Typography.Paragraph>
					<Tag color={getOrderLineItemProductionStatusTagColor(pendingDeliveryValue || '')}>{pendingDeliveryValue}</Tag>
				</Typography.Paragraph>
				<Typography.Paragraph type="secondary">
					This does not create deliveries. It only overrides the display status and filter behavior.
				</Typography.Paragraph>
			</Modal>

			<Modal
				open={!!productionQuestionBlock}
				centered
				title="Production questions blocking programming"
				onCancel={() => setProductionQuestionBlock(null)}
				footer={[
					<Button key="close" onClick={() => setProductionQuestionBlock(null)}>
						Close
					</Button>,
					<Button
						key="view"
						type="primary"
						onClick={() => {
							setProductionQuestionBlock(null);
							scrollToProductionQuestionsSection();
						}}
					>
						View Production Questions
					</Button>,
				]}
			>
				<Typography.Paragraph>{productionQuestionBlock}</Typography.Paragraph>
				<Typography.Paragraph className="mb-0">
					{PRODUCTION_QUESTION_BLOCK_HINT}
				</Typography.Paragraph>
			</Modal>

			<Modal
				open={isProgrammingTemplateModalOpen}
				centered
				title={
					pendingProgrammingTemplateValue === 'Completed'
						? 'Use Log Programming Stats'
						: 'Update Programming Template Status'
				}
				onCancel={() => {
					setIsProgrammingTemplateModalOpen(false);
					setPendingProgrammingTemplateValue(null);
				}}
				footer={[
					<Button key="scoring" onClick={goToProgramScoring}>
						Go To Scoring
					</Button>,
					<Button
						key="confirm"
						type="primary"
						danger
						loading={updateOrderLineItemResult.isLoading}
						onClick={applyProgrammingTemplateOverride}
					>
						Update anyways
					</Button>,
				]}
			>
				{pendingProgrammingTemplateValue === 'Completed' ? (
					<Typography.Paragraph>
						Programming Template is marked Completed automatically when you log
						programming stats with status <strong>Completed</strong>. Use the{' '}
						<strong>Log Programming Stats</strong> button in the Program Scoring
						section below instead of changing this dropdown.
					</Typography.Paragraph>
				) : (
					<Typography.Paragraph>
						This status should be updated by logging programming stats in the
						Program Scoring section below.
					</Typography.Paragraph>
				)}
			</Modal>
		</>
	);
};

export default OrderLineItemStatuses;
